// Question bank section UI state: per-section collapsed flag and drag-resized
// height. Persisted to localStorage under the `seq.questionBank.*` namespace.
//
// Keys in the store are strings:
//   - real section ids cast to strings (e.g. "14")
//   - the sentinel keys UNASSIGNED_KEY ("__unassigned") and ARCHIVED_KEY
//     ("__archived") for the two synthetic groups
//
// Storage shape (mirrors the in-memory state minus null/false entries):
//   localStorage['seq.questionBank.sectionHeights'] = { "14": 480, "__unassigned": 320 }
//   localStorage['seq.questionBank.sectionCollapsed'] = { "__archived": true }
//
// Writes are debounced 200 ms so a drag doesn't hammer localStorage on every
// pointermove tick.

import { reactive } from 'vue';

const STORAGE_KEY_HEIGHTS = 'seq.questionBank.sectionHeights';
const STORAGE_KEY_COLLAPSED = 'seq.questionBank.sectionCollapsed';
const STORAGE_KEY_VISITED = 'seq.questionBank.hasVisited';
const WRITE_DEBOUNCE_MS = 200;

export const UNASSIGNED_KEY = '__unassigned';
export const ARCHIVED_KEY = '__archived';

function readStorage(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch (e) {
    // Quota exceeded, JSON parse failure, or localStorage disabled
    // (privacy mode in some browsers). Fall back to an empty store.
    return {};
  }
}

function writeStorage(storageKey, obj) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(obj));
  } catch (e) {
    // Same fallback as readStorage. Swallow; the in-memory store still works.
  }
}

const initialHeights = readStorage(STORAGE_KEY_HEIGHTS);
const initialCollapsed = readStorage(STORAGE_KEY_COLLAPSED);

export const sectionUiState = reactive({
  heights: { ...initialHeights },
  collapsed: { ...initialCollapsed },
});

// First-visit detection, snapshotted at module load so every SectionGroup
// that mounts during this session sees the same default. Without this
// snapshot, a SectionGroup that mounts later (e.g. after a search filter
// reveals a new section) would see a stale-vs-fresh inconsistency.
//
// Migration: a user who already had section prefs from before this flag
// existed counts as a returning visitor — otherwise their carefully-tuned
// layout would all collapse on the upgrade.
function _detectFirstVisit() {
  let visitedFlag = false;
  try {
    visitedFlag = localStorage.getItem(STORAGE_KEY_VISITED) === 'true';
  } catch (e) { /* same fallbacks as readStorage */ }
  if (visitedFlag) return false;
  if (Object.keys(initialCollapsed).length > 0) return false;
  if (Object.keys(initialHeights).length > 0) return false;
  return true;
}

const _isFirstVisitSnapshot = _detectFirstVisit();

// Mark visited eagerly so the next page load won't be treated as fresh.
// The in-memory snapshot above stays at its captured value for this session.
try {
  localStorage.setItem(STORAGE_KEY_VISITED, 'true');
} catch (e) { /* localStorage disabled — degrade gracefully */ }

export function isFirstVisitSession() {
  return _isFirstVisitSnapshot;
}

let writeTimer = null;

function scheduleWrite() {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    // Strip null and falsey values so the persisted blob stays small
    // and a "reset" or "expand" leaves no breadcrumbs.
    const heights = {};
    for (const [k, v] of Object.entries(sectionUiState.heights)) {
      if (typeof v === 'number' && v > 0) heights[k] = v;
    }
    // Persist both `true` and `false` explicitly. Absence of an entry
    // means "no opinion yet" and falls back to the first-visit default,
    // so it's important that an explicit "expanded" doesn't get dropped
    // back to "absent" — that would re-collapse the section on every
    // refresh during a first-visit session.
    const collapsed = {};
    for (const [k, v] of Object.entries(sectionUiState.collapsed)) {
      if (v === true || v === false) collapsed[k] = v;
    }
    writeStorage(STORAGE_KEY_HEIGHTS, heights);
    writeStorage(STORAGE_KEY_COLLAPSED, collapsed);
    writeTimer = null;
  }, WRITE_DEBOUNCE_MS);
}

export function getSectionHeight(key) {
  const v = sectionUiState.heights[String(key)];
  return typeof v === 'number' ? v : null;
}

export function setSectionHeight(key, px) {
  const k = String(key);
  if (px == null) {
    delete sectionUiState.heights[k];
  } else {
    sectionUiState.heights[k] = px;
  }
  scheduleWrite();
}

/**
 * Tri-valued read: returns true (explicitly collapsed), false (explicitly
 * expanded), or null (no opinion — caller should fall back to the
 * first-visit default).
 */
export function getSectionCollapsedExplicit(key) {
  const v = sectionUiState.collapsed[String(key)];
  if (v === true) return true;
  if (v === false) return false;
  return null;
}

/**
 * True iff the section is collapsed under the current visit-context.
 * Uses the explicit value if set, otherwise the first-visit default.
 */
export function isSectionCollapsed(key) {
  const explicit = getSectionCollapsedExplicit(key);
  if (explicit !== null) return explicit;
  return _isFirstVisitSnapshot;
}

export function setSectionCollapsed(key, collapsed) {
  // Always store the explicit boolean. See the comment in scheduleWrite
  // about why absence is reserved for "no opinion yet".
  sectionUiState.collapsed[String(key)] = collapsed === true;
  scheduleWrite();
}

export function resetSectionHeight(key) {
  setSectionHeight(key, null);
}
